import React, { useMemo } from "react";
import * as THREE from "three";

// Load and cache all 4 fleet textures (no rotation — shown as-is)
function useFleetTextures() {
  return useMemo(() => {
    const loader = new THREE.TextureLoader();
    const make = (path) => {
      const t = loader.load(path);
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 16;
      t.generateMipmaps = true;
      t.minFilter = THREE.LinearMipmapLinearFilter;
      t.magFilter = THREE.LinearFilter;
      return t;
    };
    return {
      ship:      make("/assets/fleet/ship.jpg"),
      plane:     make("/assets/fleet/plane.jpg"),
      truck:     make("/assets/fleet/truck.jpg"),
      container: make("/assets/fleet/container.jpg"),
    };
  }, []);
}

// Static professional 3D image panel — NO rotation, full frame, premium edges
function FleetSlab({ texture, accentColor = "#FFD700", w = 3.6, h = 2.4, d = 0.10 }) {
  const edgeMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#1a1a1a",
        metalness: 0.9,
        roughness: 0.1,
      }),
    []
  );

  const faceMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        map: texture,
        metalness: 0.05,
        roughness: 0.4,
      }),
    [texture]
  );

  // BoxGeometry face order: +X, -X, +Y, -Y, +Z(front), -Z(back)
  const materials = [edgeMat, edgeMat, edgeMat, edgeMat, faceMat, faceMat];

  return (
    <group>
      {/* Main image slab — completely static */}
      <mesh material={materials} castShadow receiveShadow>
        <boxGeometry args={[w, h, d]} />
      </mesh>

      {/* Gold border outline */}
      <lineSegments>
        <edgesGeometry args={[new THREE.BoxGeometry(w + 0.04, h + 0.04, d + 0.02)]} />
        <lineBasicMaterial color={accentColor} transparent opacity={0.75} />
      </lineSegments>

      {/* 8 gold corner spheres */}
      {[
        [-w / 2,  h / 2,  d / 2], [ w / 2,  h / 2,  d / 2],
        [-w / 2, -h / 2,  d / 2], [ w / 2, -h / 2,  d / 2],
        [-w / 2,  h / 2, -d / 2], [ w / 2,  h / 2, -d / 2],
        [-w / 2, -h / 2, -d / 2], [ w / 2, -h / 2, -d / 2],
      ].map((pos, i) => (
        <mesh key={i} position={pos}>
          <sphereGeometry args={[0.03, 8, 8]} />
          <meshStandardMaterial color={accentColor} metalness={1} roughness={0.05} />
        </mesh>
      ))}
    </group>
  );
}

export function RealisticShip({ scale = 1 }) {
  const { ship } = useFleetTextures();
  return <group scale={scale}><FleetSlab texture={ship} accentColor="#00E5FF" /></group>;
}

export function RealisticPlane({ scale = 1 }) {
  const { plane } = useFleetTextures();
  return <group scale={scale}><FleetSlab texture={plane} accentColor="#FFD700" /></group>;
}

export function RealisticTruck({ scale = 1 }) {
  const { truck } = useFleetTextures();
  return <group scale={scale}><FleetSlab texture={truck} accentColor="#FF5500" /></group>;
}

export function RealisticContainer({ scale = 1 }) {
  const { container } = useFleetTextures();
  return <group scale={scale}><FleetSlab texture={container} accentColor="#FFD700" /></group>;
}
